import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getBankAccounts, saveBankAccount } from "../../components/Api/GmsBankAccount";
import { getBanks } from "../../components/Api/GmsBank";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  bankAccountId: 0,
  bankId: null,
  accountNumber: "",
  accountHolderName: "",
  status: true,
  isDefault: false,
  createdBy: 1,
  createdOn: new Date().toISOString(),
  modifiedBy: null,
  modifiedOn: null,
};

const emptyFilter = {
  bankAccountId: null,
  bankId: null,
  accountNumber: "",
  accountHolderName: "",
  status: null,
  isDefault: null,
};

export default function BankAccountPage() {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Get user session
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const session = getUserSession();

  const formatDateTime = (dateTimeStr) => {
    try {
      if (!dateTimeStr) return "N/A";
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadBanks();
      loadBankAccounts();
    }
  }, [permissionsLoading, canView, page, pageSize]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsBankAccount');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsBankAccount');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsBankAccount');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadBanks = async () => {
    try {
      const response = await getBanks({ status: true, page: 1, pageSize: 1000 });
      setBanks(response?.banks || []);
    } catch (error) {
      console.error("Error loading banks:", error);
    }
  };

  const loadBankAccounts = async (filterData = {}) => {
    setLoading(true);
    try {
      const filterPayload = {
        ...filterData,
        page: page,
        pageSize: pageSize,
      };
      const response = await getBankAccounts(filterPayload);
      setBankAccounts(response?.bankAccounts || []);
      setTotalCount(response?.totalCount || 0);
    } catch (error) {
      Swal.fire("Error", "Failed to load bank accounts: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (!form.bankId) {
        Swal.fire("Error", "Bank is required", "error");
        return;
      }

      if (!form.accountNumber || form.accountNumber.trim() === "") {
        Swal.fire("Error", "Account Number is required", "error");
        return;
      }

      if (!form.accountHolderName || form.accountHolderName.trim() === "") {
        Swal.fire("Error", "Account Holder Name is required", "error");
        return;
      }

      const bankAccountData = {
        ...form,
        createdBy: isEditing ? form.createdBy : session.userId,
        modifiedBy: isEditing ? session.userId : null,
        modifiedOn: isEditing ? new Date().toISOString() : null,
      };

      const result = await saveBankAccount(bankAccountData);

      if (result.id > 0) {
        Swal.fire("Success", result.message || "Bank Account saved successfully", "success");
        setForm(defaultForm);
        setIsEditing(false);
        loadBankAccounts(filter);
      } else {
        Swal.fire("Error", result.message || "Failed to save bank account", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to save bank account", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (bankAccount) => {
    try {
      setViewLoadingRow(bankAccount.bankAccountId || bankAccount.BankAccountId);
      setViewData(bankAccount);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire("Error", "Failed to view bank account details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (bankAccount) => {
    const bankAccountId = bankAccount.bankAccountId || bankAccount.BankAccountId;
    const bankId = bankAccount.bankId || bankAccount.BankId;
    setForm({
      bankAccountId: bankAccountId,
      bankId: bankId,
      accountNumber: bankAccount.accountNumber || bankAccount.AccountNumber || "",
      accountHolderName: bankAccount.accountHolderName || bankAccount.AccountHolderName || "",
      status: bankAccount.status !== undefined ? bankAccount.status : (bankAccount.Status !== undefined ? bankAccount.Status : true),
      isDefault: bankAccount.isDefault !== undefined ? bankAccount.isDefault : (bankAccount.IsDefault !== undefined ? bankAccount.IsDefault : false),
      createdBy: bankAccount.createdBy || bankAccount.CreatedBy || session.userId,
      createdOn: bankAccount.createdOn || bankAccount.CreatedOn || new Date().toISOString(),
      modifiedBy: null,
      modifiedOn: null,
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadBankAccounts(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadBankAccounts(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Bank options for dropdown
  const bankOptions = useMemo(() => {
    return banks.map(b => {
      const bankId = b.bankId || b.BankId;
      const bankCode = b.bankCode || b.BankCode || '';
      const bankNameEn = b.bankNameEn || b.BankNameEn || '';
      return {
        value: bankId,
        label: `${bankCode} - ${bankNameEn}`.trim(),
      };
    });
  }, [banks]);

  // Options for searchable dropdowns
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const defaultOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const pages = [];
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="d-flex gap-1">
          <Button
            color="primary"
            size="sm"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            Previous
          </Button>
          {pages.map(pageNum => (
            <Button
              key={pageNum}
              color={pageNum === page ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
          <Button
            color="primary"
            size="sm"
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Bank Account Management" breadcrumbItem="Bank Account Management" />
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {/* Form Section - Only show if user has edit permission */}
                {canEdit && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        {isEditing ? "Edit Bank Account" : "Add New Bank Account"}
                      </h5>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Bank <span className="text-danger">*</span></Label>
                              <Select
                                value={bankOptions.find(opt => opt.value === form.bankId) || null}
                                onChange={(option) => handleFormChange("bankId", option?.value || null)}
                                options={bankOptions}
                                isClearable
                                placeholder="Select Bank..."
                                styles={selectStyles}
                                isSearchable
                              />
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Account Number <span className="text-danger">*</span></Label>
                              <Input
                                name="accountNumber"
                                value={form.accountNumber}
                                onChange={(e) => handleFormChange("accountNumber", e.target.value)}
                                required
                              />
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Account Holder Name <span className="text-danger">*</span></Label>
                              <Input
                                name="accountHolderName"
                                value={form.accountHolderName}
                                onChange={(e) => handleFormChange("accountHolderName", e.target.value)}
                                required
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <div className="form-check">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                id="status"
                                name="status"
                                checked={form.status}
                                onChange={(e) => handleFormChange("status", e.target.checked)}
                                disabled={!canDelete}
                              />
                              <Label className="form-check-label" for="status">
                                Active
                              </Label>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="form-check">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                id="isDefault"
                                name="isDefault"
                                checked={form.isDefault}
                                onChange={(e) => handleFormChange("isDefault", e.target.checked)}
                              />
                              <Label className="form-check-label" for="isDefault">
                                Is Default
                              </Label>
                            </div>
                          </Col>
                        </Row>

                        <div className="d-flex gap-2">
                          <Button
                            type="submit"
                            color="success"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <ClipLoader size={20} color="white" />{" "}
                                <span className="ms-2">Saving...</span>
                              </>
                            ) : isEditing ? (
                              "Update Bank Account"
                            ) : (
                              "Save Bank Account"
                            )}
                          </Button>
                          {isEditing && (
                            <Button
                              type="button"
                              color="secondary"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </Form>
                    </CardBody>
                  </Card>
                )}

                {/* Filter Section */}
                <Card className="mb-4">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">Filter Bank Accounts</h5>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Bank</Label>
                              <Select
                                isClearable
                                value={
                                  filter.bankId
                                    ? bankOptions.find(opt => opt.value === filter.bankId)
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange("bankId", option ? option.value : null)
                                }
                                options={bankOptions}
                                styles={selectStyles}
                                isSearchable
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Account Number</Label>
                              <Input
                                type="text"
                                value={filter.accountNumber}
                                onChange={(e) => handleFilterChange("accountNumber", e.target.value)}
                                placeholder="Search account number..."
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Account Holder Name</Label>
                              <Input
                                type="text"
                                value={filter.accountHolderName}
                                onChange={(e) => handleFilterChange("accountHolderName", e.target.value)}
                                placeholder="Search holder name..."
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Status</Label>
                              <Select
                                isClearable
                                value={
                                  filter.status !== null
                                    ? statusOptions.find(
                                        (option) => option.value === filter.status
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange("status", option ? option.value : null)
                                }
                                options={statusOptions}
                                styles={selectStyles}
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply Filter
                          </Button>
                          <Button color="secondary" onClick={handleClearFilter}>
                            Clear Filter
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Bank Accounts List */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">Bank Accounts List ({totalCount} records)</h5>
                    {loading ? (
                      <div className="text-center py-4">
                        <ClipLoader color="#556ee6" loading={loading} size={50} />
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table bordered>
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Bank</th>
                                <th>Account Number</th>
                                <th>Account Holder Name</th>
                                <th>Status</th>
                                <th>Is Default</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bankAccounts.length === 0 ? (
                                <tr>
                                  <td colSpan="7" className="text-center">
                                    No bank accounts found
                                  </td>
                                </tr>
                              ) : (
                                bankAccounts.map((bankAccount, index) => {
                                  const bankAccountId = bankAccount.bankAccountId || bankAccount.BankAccountId;
                                  const bankNameEn = bankAccount.bankNameEn || bankAccount.BankNameEn || 'N/A';
                                  const accountNumber = bankAccount.accountNumber || bankAccount.AccountNumber || 'N/A';
                                  const accountHolderName = bankAccount.accountHolderName || bankAccount.AccountHolderName || 'N/A';
                                  const status = bankAccount.status !== undefined ? bankAccount.status : (bankAccount.Status !== undefined ? bankAccount.Status : true);
                                  const isDefault = bankAccount.isDefault !== undefined ? bankAccount.isDefault : (bankAccount.IsDefault !== undefined ? bankAccount.IsDefault : false);
                                  
                                  return (
                                    <tr key={bankAccountId}>
                                      <td>{(page - 1) * pageSize + index + 1}</td>
                                      <td>{bankNameEn}</td>
                                      <td>{accountNumber}</td>
                                      <td>{accountHolderName}</td>
                                      <td>
                                        <span className={`badge ${status ? 'bg-success' : 'bg-danger'}`}>
                                          {status ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                      <td>
                                        <span className={`badge ${isDefault ? 'bg-info' : 'bg-secondary'}`}>
                                          {isDefault ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleView(bankAccount)}
                                            disabled={viewLoadingRow === bankAccountId}
                                          >
                                            {viewLoadingRow === bankAccountId ? (
                                              <ClipLoader size={16} color="#ffffff" />
                                            ) : (
                                              <Eye size={16} />
                                            )}
                                          </Button>
                                          {canEdit && (
                                            <Button
                                              color="warning"
                                              size="sm"
                                              onClick={() => handleEdit(bankAccount)}
                                              disabled={editLoadingRow === bankAccountId}
                                            >
                                              {editLoadingRow === bankAccountId ? (
                                                <ClipLoader size={16} color="#ffffff" />
                                              ) : (
                                                <SquarePen size={16} />
                                              )}
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </div>
                        {totalCount > 0 && <Pagination />}
                      </>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* View Modal */}
        <Modal isOpen={showViewModal} toggle={() => setShowViewModal(false)} size="lg">
          <ModalHeader toggle={() => setShowViewModal(false)}>Bank Account Details</ModalHeader>
          <ModalBody>
            {viewData && (
              <Row>
                <Col md={6}>
                  <p><strong>Bank:</strong> {viewData.bankNameEn || viewData.BankNameEn || 'N/A'}</p>
                  <p><strong>Account Number:</strong> {viewData.accountNumber || viewData.AccountNumber || 'N/A'}</p>
                  <p><strong>Account Holder Name:</strong> {viewData.accountHolderName || viewData.AccountHolderName || 'N/A'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> 
                    <span className={`badge ms-2 ${(viewData.status !== undefined ? viewData.status : (viewData.Status !== undefined ? viewData.Status : true)) ? 'bg-success' : 'bg-danger'}`}>
                      {(viewData.status !== undefined ? viewData.status : (viewData.Status !== undefined ? viewData.Status : true)) ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                  <p><strong>Is Default:</strong> 
                    <span className={`badge ms-2 ${(viewData.isDefault !== undefined ? viewData.isDefault : (viewData.IsDefault !== undefined ? viewData.IsDefault : false)) ? 'bg-info' : 'bg-secondary'}`}>
                      {(viewData.isDefault !== undefined ? viewData.isDefault : (viewData.IsDefault !== undefined ? viewData.IsDefault : false)) ? 'Yes' : 'No'}
                    </span>
                  </p>
                  <p><strong>Created On:</strong> {formatDateTime(viewData.createdOn || viewData.CreatedOn)}</p>
                  <p><strong>Modified On:</strong> {formatDateTime(viewData.modifiedOn || viewData.ModifiedOn)}</p>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}

